import type { ReactNode } from 'react';
import markOrange from '../assets/brand/mark-orange.svg';

/**
 * The screens a person meets before the app exists for them: sign-in,
 * sign-up, onboarding and the two invite-accept pages.
 *
 * Porcelain Orange: flat porcelain page, one white card with a warm
 * hairline, soft filled fields, and a single orange pill as the only loud
 * element. No glows, no gradients — warmth comes from the orange itself.
 * Shared by every pre-app screen so the first things anyone sees are the
 * same thing twice.
 */
export function AuthShell({
  eyebrow,
  eyebrowTone = 'primary',
  title,
  titleSize = 'lg',
  subtitle,
  children,
  footer,
  center = true,
  headerStart = false,
  mark,
}: {
  /** The tiny all-caps line above the heading — "Create your household". */
  eyebrow?: string;
  eyebrowTone?: 'primary' | 'muted';
  title: ReactNode;
  /** The mock sizes the welcome headings at 38px and the invite ones at 34px. */
  titleSize?: 'lg' | 'md';
  subtitle?: string;
  children: ReactNode;
  /** Pinned to the foot of the page — the sign-in link, the lock note. */
  footer?: ReactNode;
  /** Sign-in and the invites are vertically centered; sign-up and onboarding
   *  run top-to-bottom so their footers can rest on the bottom edge. */
  center?: boolean;
  /** Onboarding's header hugs the start edge; every other screen centers it. */
  headerStart?: boolean;
  /** What sits above the heading. Defaults to the brand mark in its white
   *  tile; pass `false` for no mark (onboarding) or a ReactNode for the
   *  co-owner invite's brand chip. */
  mark?: ReactNode | false;
}) {
  return (
    <div className="bg-background">
      <div className="mx-auto flex min-h-[100dvh] w-full max-w-sm flex-col px-6 pb-10 pt-12">
        <div className={`flex flex-1 flex-col ${center ? 'items-center justify-center py-4' : ''}`}>
          {mark === false ? null : (mark ?? <BrandTile className="mx-auto mb-10" />)}
          <div className={`mb-8 w-full ${headerStart ? 'text-start' : 'text-center'}`}>
            {eyebrow && (
              <p
                className={`mb-3 text-[11px] font-bold uppercase tracking-[0.2em] ${
                  eyebrowTone === 'primary' ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                {eyebrow}
              </p>
            )}
            <h1
              className={`font-heading font-semibold leading-[1.05] tracking-[-0.05em] text-foreground ${
                titleSize === 'lg' ? 'text-[38px]' : 'text-[34px]'
              }`}
            >
              {title}
            </h1>
            {subtitle && (
              <p
                className={`mt-3 text-[15px] font-medium leading-relaxed tracking-tight text-muted-foreground ${
                  headerStart ? '' : 'mx-auto max-w-[290px]'
                }`}
              >
                {subtitle}
              </p>
            )}
          </div>
          {children}
        </div>
        {footer && <div className="w-full pt-8 text-center">{footer}</div>}
      </div>
    </div>
  );
}

/** The brand mark in its white tile — the mock's generic home icon, swapped
 *  for the real thing. */
function BrandTile({ className = '' }: { className?: string }) {
  return (
    <div
      className={`flex h-14 w-14 items-center justify-center rounded-[20px] border border-border bg-card shadow-[0_4px_12px_rgba(0,0,0,0.04)] ${className}`}
    >
      <img src={markOrange} alt="" className="h-7 w-auto" />
    </div>
  );
}

/**
 * A labelled field. The approved design carries no visible labels — the
 * placeholder names the field — so the label is kept for screen readers
 * only. The caller owns the control's id because the control is passed in
 * as children — PasswordInput, for one, wraps its input in a div, so
 * wrapping the whole child in the <label> would also swallow its Show/Hide
 * button.
 */
export function AuthField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="sr-only">
        {label}
      </label>
      {children}
    </div>
  );
}

/** The white card every one of these screens is built from. */
export const AUTH_CARD =
  'w-full rounded-[28px] border border-border bg-card p-6 shadow-[0_8px_30px_rgba(0,0,0,0.02)]';

/** His 56px field: soft grey fill, no border, room for a thumb. */
export const AUTH_INPUT =
  'h-14 w-full rounded-[20px] border-none bg-secondary px-5 text-[15px] font-medium text-foreground transition-all placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/20';

/** The orange pill — the single loud action per screen. */
export const AUTH_BUTTON =
  'h-14 w-full rounded-full bg-primary text-[16px] font-bold tracking-wide text-primary-foreground shadow-lg shadow-primary/20 transition-all active:scale-[0.98] disabled:opacity-40';
